import {
  clearCustomComputeCache,
  deinitCustomIndicatorWasmInstances,
  runCustomIndicatorLifecycle,
} from "./compiled-template";
import { useIndicatorCompileStore } from "./runtime/compile-store";
import { useIndicatorLoggerStore } from "./runtime/logger-store";
import {
  editorPreviewIndicatorId,
  editorPreviewInstanceId,
} from "./editor-preview-ids";
import type { CompileResult } from "./runtime-types";
import {
  syncIndicators,
  forceChartIndicatorRecalc,
  removeAppliedIndicatorFromChart,
  resolveCustomMainChartOverlay,
  type ChartIndicatorApi,
  type IndicatorSyncStateMap,
} from "@/lib/chart-helpers/chart-indicators";

export interface ReloadPreviewIndicatorArgs {
  chart: ChartIndicatorApi | null;
  syncState: IndicatorSyncStateMap;
  projectId: string;
  displayName: string;
  compileResult: CompileResult | undefined;
  appliedInputValues: Record<string, number | string>;
  hasChartData: boolean;
}

const reloadChains = new Map<string, Promise<void>>();

function logLifecycleResult(
  phase: string,
  result: { success?: boolean; error?: string }
): boolean {
  if (result.success === true) return true;
  let msg =
    result.error ??
    `failed (${phase} returned success=${String(result.success)})`;
  if (
    phase === "init" &&
    /not compiled|not loaded/i.test(msg)
  ) {
    msg = `WASM project not loaded (preview hydration may have failed). ${msg}`;
  }
  useIndicatorLoggerStore.getState().appendLogger(`[${phase}] ${msg}`);
  return false;
}

async function reloadPreviewIndicatorInner(
  args: ReloadPreviewIndicatorArgs
): Promise<void> {
  const {
    chart,
    syncState,
    projectId,
    displayName,
    compileResult,
    appliedInputValues,
    hasChartData,
  } = args;

  if (!chart || !projectId) return;

  const instanceId = editorPreviewInstanceId(projectId);
  const indicatorId = editorPreviewIndicatorId(projectId);
  const inputVals = appliedInputValues as Record<
    string,
    number | string | boolean
  >;

  const applied = syncState.get(instanceId);

  // 1. Detach from KLine first so calc callbacks cannot race WASM init/deinit.
  if (applied) {
    try {
      removeAppliedIndicatorFromChart(chart, applied);
    } catch {
      /* chart may be disposed */
    }
    syncState.delete(instanceId);
  }

  clearCustomComputeCache(instanceId);

  const revision =
    useIndicatorCompileStore.getState().getCompileRevision(projectId);

  if (revision > 0) {
    await deinitCustomIndicatorWasmInstances(projectId, instanceId, inputVals);
  }

  if (!hasChartData || !compileResult?.success) return;

  useIndicatorLoggerStore
    .getState()
    .appendLogger(`--- indicator reload (r${revision}) ---`);

  const initResult = await runCustomIndicatorLifecycle(
    projectId,
    instanceId,
    inputVals,
    "init"
  );
  if (!logLifecycleResult("init", initResult)) return;

  syncIndicators(
    chart,
    { isOverlay: resolveCustomMainChartOverlay(compileResult) },
    [
      {
        id: instanceId,
        indicatorId,
        name: displayName,
        settings: {
          values: appliedInputValues,
        },
        visible: true,
      },
    ],
    syncState
  );

  forceChartIndicatorRecalc(chart);
}

/**
 * Serialized full reload of the editor preview custom indicator.
 * Parallel reloads were racing and preventing OnInit / Logger.Print from running.
 */
export function reloadPreviewIndicator(
  args: ReloadPreviewIndicatorArgs
): Promise<void> {
  const key = args.projectId;
  const previous = reloadChains.get(key) ?? Promise.resolve();
  const next = previous
    .then(() => reloadPreviewIndicatorInner(args))
    .catch((e) => {
      const message = e instanceof Error ? e.message : String(e);
      useIndicatorLoggerStore.getState().appendLogger(`[reload] ${message}`);
    });
  reloadChains.set(key, next);
  void next.finally(() => {
    if (reloadChains.get(key) === next) reloadChains.delete(key);
  });
  return next;
}
